import Meta from '../components/Meta'
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useParams } from 'react-router'
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Col,
  Form,
  ListGroup,
  Row,
} from 'react-bootstrap'
import {
  CalendarDate,
  CalendarEvent,
  CameraVideo,
  CashCoin,
  CheckCircle,
  CheckCircleFill,
  ArrowCounterclockwise,
  ChevronLeft,
  DropletFill,
  DropletHalf,
  FileEarmark,
  FileEarmarkPlus,
  GenderFemale,
  GenderMale,
  Headset,
  PencilSquare,
  Person,
  PersonVideo3,
  Radioactive,
  Tag,
  Telephone,
  Trash,
  UpcScan,
  Upload,
  X,
  Box,
  Envelope,
  Phone,
  Archive,
  Upc,
} from 'react-bootstrap-icons'
import { useNavigate } from 'react-router-dom'
import {
  Discount_Type_Enum_Enum,
  Gender_Enum,
  GetOrderQuery,
  GetOrderShipmentLabelDocument,
  GetOrderShipmentLabelQuery,
  GetOrderShipmentLabelQueryVariables,
  Order_State_Enum_Enum,
  useDeleteOrderTestResultMutation,
  useGetOrderQuery,
  usePingMutation,
  useRestoreOrderConsultationMutation,
  useRestoreOrderTestMutation,
  useRestoreOrderTrainingMutation,
  useSendOrderTestMutation,
  useSoftDeleteOrderConsultationMutation,
  useSoftDeleteOrderTestMutation,
  useSoftDeleteOrderTrainingMutation,
  useUpdateOrderConsultationFinishedOnMutation,
  useUpdateOrderTotalCostMutation,
} from '../generated/urql.administrator'
import moment from 'moment'
import { AuthContext } from '../provider/auth'
import {
  getOrderStateLabel,
  OrderState2BgVariant,
  OrderState2Label,
} from '../lib/order'
import { StorageUrl } from '../const'
import { isIdentity } from '../lib/identity'
import TestResultConfirmationModal, {
  TestResultConfirmationModalType,
  ValidTestResult,
} from '../components/TestResultConfirmationModal'
import { FilenamePattern } from '../lib/testResult'
import OrderNurseModal, {
  OrderNurseModalType,
} from '../components/OrderNurseModal'
import OrderTestModal, {
  UpdateOrderTestModalData,
} from '../components/UpdateOrderTestModal'
import ChangeOrderStatusModal, {
  ChangeOrderStatusModalType,
} from '../components/ChangeOrderStatusModal'
import DeleteOrderItemModal from '../components/DeleteOrderItemModal'
import { inpostApi } from '../lib/axios'
import { useClient } from 'urql'
import { downloadPdf } from '../lib/files'
import toastsService from '../lib/toastsService'

const pageTitle = 'Szczegóły badania'

type OrderTest = NonNullable<GetOrderQuery['order_by_pk']>['orderTests'][number]
type OrderConsultation = NonNullable<
  GetOrderQuery['order_by_pk']
>['orderConsultations'][number]
type OrderTraining = NonNullable<
  GetOrderQuery['order_by_pk']
>['orderTrainings'][number]

function isOrderTest(
  item: OrderTest | OrderConsultation | OrderTraining
): item is OrderTest {
  return 'test' in item
}

function isOrderConsultation(
  item: OrderTest | OrderConsultation | OrderTraining
): item is OrderConsultation {
  return 'consultation' in item
}

function isOrderTraining(
  item: OrderTest | OrderConsultation | OrderTraining
): item is OrderTraining {
  return 'training' in item
}

function renderIdentity(identity: unknown) {
  if (!isIdentity(identity)) {
    return 'Nieznany rodzaj tożsamości'
  }

  switch (identity.type) {
    case 'id_card':
      return (
        <span>
          {identity.value}{' '}
          <small>
            (dowód tożsamości, {identity.countryCode}, {identity.dateOfBirth})
          </small>
        </span>
      )
    case 'passport':
      return (
        <span>
          {identity.value}{' '}
          <small>
            (paszport, {identity.countryCode}, {identity.dateOfBirth})
          </small>
        </span>
      )
    case 'pesel':
      return (
        <span>
          {identity.value} <small>(PESEL)</small>
        </span>
      )
  }
}

function getDiscountBadge(type: Discount_Type_Enum_Enum) {
  switch (type) {
    case Discount_Type_Enum_Enum.SomeItems:
      return <Badge bg="secondary">Wiele produktów</Badge>
    case Discount_Type_Enum_Enum.AllTests:
      return <Badge bg="success">Wszystkie badania</Badge>
    case Discount_Type_Enum_Enum.AllConsultations:
      return <Badge bg="success">Wszystkie konsultacje</Badge>
    case Discount_Type_Enum_Enum.AllTrainings:
      return <Badge bg="success">Wszystkie szkolenia</Badge>
    case Discount_Type_Enum_Enum.AllItems:
      return <Badge bg="success">Wszystkie produkty</Badge>
    case Discount_Type_Enum_Enum.WholeOrder:
      return <Badge bg="primary">Całe zamówienie</Badge>
    case Discount_Type_Enum_Enum.WholeOrderWithItems:
      return <Badge bg="warning">Całe zamówienie (produkty)</Badge>
    case Discount_Type_Enum_Enum.CollectionCost:
      return <Badge bg="secondary">Koszt pobrania</Badge>
    default:
      return <span>?</span>
  }
}

type InpostStatus = {
  name: string
  title: string
  description: string
}

type DeleteItemModalState = {
  isVisible: boolean
  itemType: 'test' | 'consultation' | 'training'
  itemId: number
  itemName: string
  itemCost: number
  itemCostBeforeDiscount?: number | null
}

type OrderData = NonNullable<GetOrderQuery['order_by_pk']>

function recalculateOrderTotal(
  order: OrderData,
  override: {
    id: number
    type: 'test' | 'consultation' | 'training'
    deletedAt: string | null
  }
) {
  const applyOverride = <T extends { id: number; deletedAt?: string | null }>(
    items: T[],
    type: typeof override.type,
    targetType: typeof override.type
  ): T[] =>
    type === targetType
      ? items.map((item) =>
          item.id === override.id
            ? { ...item, deletedAt: override.deletedAt }
            : item
        )
      : items

  const tests = applyOverride(order.orderTests, override.type, 'test')
  const consultations = applyOverride(
    order.orderConsultations,
    override.type,
    'consultation'
  )
  const trainings = applyOverride(order.orderTrainings, override.type, 'training')
  const col = order.orderCollections[0]

  const totalCost =
    tests.filter((t) => !t.deletedAt).reduce((s, t) => s + (t.cost ?? 0), 0) +
    consultations
      .filter((c) => !c.deletedAt)
      .reduce((s, c) => s + (c.cost ?? 0), 0) +
    trainings
      .filter((tr) => !tr.deletedAt)
      .reduce((s, tr) => s + (tr.cost ?? 0), 0) +
    (col
      ? col.collectionCost + col.deliveryCost + col.collectionKitCost
      : 0) +
    order.orderShippings.reduce((s, sh) => s + sh.cost, 0)

  const costBeforeDiscount =
    order.costBeforeDiscount != null
      ? tests
          .filter((t) => !t.deletedAt)
          .reduce((s, t) => s + (t.costBeforeDiscount ?? t.cost ?? 0), 0) +
        consultations
          .filter((c) => !c.deletedAt)
          .reduce((s, c) => s + (c.cost ?? 0), 0) +
        trainings
          .filter((tr) => !tr.deletedAt)
          .reduce((s, tr) => s + (tr.cost ?? 0), 0) +
        (col
          ? (col.collectionCostBeforeDiscount ?? col.collectionCost) +
            col.deliveryCost +
            col.collectionKitCost
          : 0) +
        order.orderShippings.reduce(
          (s, sh) => s + (sh.costBeforeDiscount ?? sh.cost),
          0
        )
      : null

  return { totalCost, costBeforeDiscount }
}

const Order: FC = () => {
  const { id } = useParams()
  const client = useClient()
  const navigate = useNavigate()
  const auth = useContext(AuthContext)
  const [{ data }, refetchGetOrder] = useGetOrderQuery({
    variables: { id: parseInt(id!) },
    requestPolicy: 'network-only',
  })
  const [generalError, setGeneralError] = useState<string>()
  const [, updateOrderConsultationFinishedOn] =
    useUpdateOrderConsultationFinishedOnMutation()
  const [, softDeleteOrderTest] = useSoftDeleteOrderTestMutation()
  const [, softDeleteOrderConsultation] = useSoftDeleteOrderConsultationMutation()
  const [, softDeleteOrderTraining] = useSoftDeleteOrderTrainingMutation()
  const [, restoreOrderTest] = useRestoreOrderTestMutation()
  const [, restoreOrderConsultation] = useRestoreOrderConsultationMutation()
  const [, restoreOrderTraining] = useRestoreOrderTrainingMutation()
  const [, updateOrderTotalCost] = useUpdateOrderTotalCostMutation()
  const [documents, setDocuments] = useState<string[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [{ fetching: sendOrderTestFetching }, sendOrderTest] =
    useSendOrderTestMutation()
  const [, deleteOrderTestResult] = useDeleteOrderTestResultMutation()
  const [checkedOrderTestIds, setCheckedOrderTestIds] = useState<number[]>([])
  const [, ping] = usePingMutation()
  const testResultConfirmationModalRef =
    useRef<TestResultConfirmationModalType>(null)
  const orderNurseModalRef = useRef<OrderNurseModalType>(null)

  const [updateOrderTestModal, setUpdateOrderTestModal] =
    useState<UpdateOrderTestModalData>({
      isVisible: false,
    })
  const [inpostStatuses, setInpostStatuses] = useState<InpostStatus[]>([])
  const [deleteItemModal, setDeleteItemModal] = useState<DeleteItemModalState>({
    isVisible: false,
    itemType: 'test',
    itemId: 0,
    itemName: '',
    itemCost: 0,
  })
  const [deleteItemLoading, setDeleteItemLoading] = useState(false)
  const changeOrderStatusModalRef = useRef<ChangeOrderStatusModalType>(null)

  const order = data?.order_by_pk
  const orderCollection = order?.orderCollections[0]
  const shippings = order?.orderShippings || []

  const orderItems = useMemo(
    () => [
      ...(order?.orderTests || []),
      ...(order?.orderConsultations || []),
      ...(order?.orderTrainings || []),
    ],
    [order?.orderConsultations, order?.orderTests, order?.orderTrainings]
  )

  const uploadOrderTestResult = useCallback(
    async (orderTests: ValidTestResult['orderTests'], files: FileList) => {
      setGeneralError(undefined)

      await ping({}, { requestPolicy: 'network-only' })

      if (orderTests.length === 1) {
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          const matches = file.name.match(FilenamePattern)
          if (matches) {
            const [, tubeCode, , laboratoryCode] = matches
            if (
              orderTests[0].tubeCode === tubeCode ||
              orderTests[0].laboratoryCode === laboratoryCode
            ) {
              continue
            }
          }

          setGeneralError(`Nieprawidłowa nazwa pliku ${file.name}`)
          return
        }
      }

      testResultConfirmationModalRef.current?.show(
        [...files].map((file) => ({
          file,
          orderTests,
        }))
      )
    },
    [ping]
  )

  const downloadFile = useCallback(
    async (id: number) => {
      const response = await fetch(`${StorageUrl}/files/${id}`, {
        headers: {
          Authorization: `Bearer ${auth.accessToken}`,
        },
      })

      const blob = await response.blob()
      return window.URL.createObjectURL(blob)
    },
    [auth.accessToken]
  )

  const openFile = useCallback(
    async (id: number) => {
      try {
        window.location.assign(await downloadFile(id))
      } catch (error) {
        console.error(error)
      }
    },
    [downloadFile]
  )

  useEffect(() => {
    if (order) {
      Promise.all(
        order.documents.map(async (document) => downloadFile(document.fileId))
      )
        .then(setDocuments)
        .catch(console.error)
    }
  }, [order, downloadFile])

  const getInpostStatusByStatusName = (name: string) =>
    inpostStatuses.find((i) => i.name === name)

  const getInpostStatuses = async () => {
    try {
      const response = await inpostApi().get('/statuses')
      setInpostStatuses(response.data.items || [])
    } catch (error) {
      setInpostStatuses([])
    }
  }

  const getShipmentLabel = async (
    shipmentId: number,
    trackingNumber?: string | null
  ) => {
    const fileName = trackingNumber ? `${trackingNumber}.pdf` : 'etykieta.pdf'
    const { data, error } = await client
      .query<GetOrderShipmentLabelQuery, GetOrderShipmentLabelQueryVariables>(
        GetOrderShipmentLabelDocument,
        { shipmentId }
      )
      .toPromise()

    if (error) {
      toastsService.add({
        title: 'Błąd',
        message: 'Nie udało się pobrać pliku',
        variant: 'danger',
      })
    }

    const fileType = data?.administrator_order_shipment_label.fileType
    const fileBase64 = data?.administrator_order_shipment_label.fileBase64
    if (fileBase64 && fileType === 'pdf') {
      downloadPdf(fileBase64, fileName)
    } else {
      toastsService.add({
        title: 'Błąd',
        message: 'Nie udało się pobrać pliku',
        variant: 'danger',
      })
    }
  }

  useEffect(() => {
    getInpostStatuses()
  }, [])

  const applyItemMutationAndUpdateTotal = async (
    mutateFn: () => Promise<{ error?: Error }>,
    override: Parameters<typeof recalculateOrderTotal>[1]
  ) => {
    if (!order) return
    setGeneralError(undefined)

    const { error } = await mutateFn()
    if (error) {
      setGeneralError(error.message)
      return false
    }

    const { totalCost, costBeforeDiscount } = recalculateOrderTotal(
      order,
      override
    )
    const { error: updateError } = await updateOrderTotalCost({
      id: order.id,
      totalCost,
      costBeforeDiscount,
    })
    if (updateError) {
      setGeneralError(updateError.message)
      return false
    }

    return true
  }

  const handleDeleteItem = async () => {
    if (!order) return
    setDeleteItemLoading(true)

    const deletedAt = new Date().toISOString()
    const { itemType, itemId } = deleteItemModal

    const mutateFn =
      itemType === 'test'
        ? () => softDeleteOrderTest({ id: itemId, deletedAt })
        : itemType === 'consultation'
        ? () => softDeleteOrderConsultation({ id: itemId, deletedAt })
        : () => softDeleteOrderTraining({ id: itemId, deletedAt })

    const ok = await applyItemMutationAndUpdateTotal(mutateFn, {
      id: itemId,
      type: itemType,
      deletedAt,
    })

    if (ok) {
      setDeleteItemModal((prev) => ({ ...prev, isVisible: false }))
      refetchGetOrder({ requestPolicy: 'network-only' })
    }
    setDeleteItemLoading(false)
  }

  const handleRestoreItem = async (
    itemType: 'test' | 'consultation' | 'training',
    itemId: number
  ) => {
    if (!order) return

    const mutateFn =
      itemType === 'test'
        ? () => restoreOrderTest({ id: itemId })
        : itemType === 'consultation'
        ? () => restoreOrderConsultation({ id: itemId })
        : () => restoreOrderTraining({ id: itemId })

    const ok = await applyItemMutationAndUpdateTotal(mutateFn, {
      id: itemId,
      type: itemType,
      deletedAt: null,
    })

    if (ok) {
      refetchGetOrder({ requestPolicy: 'network-only' })
    }
  }

  if (!order) {
    return <div>Ładowanie...</div>
  }

  return (
    <div>
      <Meta title={pageTitle} />
      <div className="mt-3 mb-3 d-flex justify-content-between">
        <ButtonGroup>
          <Button
            className="d-flex align-items-center"
            onClick={() => navigate('/orders')}
          >
            <ChevronLeft size={20} />
            &nbsp;Wróć
          </Button>
        </ButtonGroup>
      </div>
      {generalError && <Alert variant="danger">{generalError}</Alert>}
      <>
        <div className="d-flex">
          <div className="flex-grow-1 w-50">
            <h4 className="mt-5">Informacje</h4>
            {!!orderCollection && (
              <>
                {orderCollection.isVoucher ? (
                  <p>
                    <span className="fw-bold">Voucher ważny do: </span>
                    {moment(orderCollection.voucherValidUntil).format(
                      'D MMM YYYY HH:mm'
                    )}
                  </p>
                ) : (
                  !!orderCollection.collectionDate && (
                    <p>
                      <span className="fw-bold">Data zlecenia: </span>
                      {moment(orderCollection.collectionDate).format(
                        'D MMM YYYY HH:mm'
                      )}
                    </p>
                  )
                )}
                <p className="d-flex align-items-center gap-1">
                  <span className="fw-bold">Osoba pobierająca: </span>
                  {orderCollection.nurse?.deletedAt && (
                    <del>{orderCollection.nurse?.name}</del>
                  )}
                  {!orderCollection.nurse?.deletedAt && (
                    <span>{orderCollection.nurse?.name}</span>
                  )}
                  {orderCollection.nurse && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() =>
                        orderNurseModalRef.current?.show({ orderId: order.id })
                      }
                    >
                      <PencilSquare size={18} />
                    </Button>
                  )}
                </p>
              </>
            )}
            <p>
              <span className="fw-bold">Email klienta: </span>
              <a href={`/client/${order.client.id}`}>{order.client.email}</a>
            </p>
            {order.client.clientProfiles.map((profile) => (
              <p key={profile.identity}>
                <span className="fw-bold">Główny profil: </span>
                <span>
                  {profile.firstName} {profile.lastName}
                </span>
              </p>
            ))}
            <p className="d-flex align-items-center gap-2">
              <span className="fw-bold">Status: </span>
              <Badge
                bg={
                  OrderState2BgVariant[
                    order.currentState as Order_State_Enum_Enum
                  ]
                }
              >
                {getOrderStateLabel(order)}
              </Badge>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() =>
                  changeOrderStatusModalRef.current?.show({
                    orderId: order.id,
                    currentState: order.currentState as Order_State_Enum_Enum,
                  })
                }
              >
                <PencilSquare size={14} className="me-1" />
                Zmień status
              </Button>
            </p>

            {!!orderCollection && (
              <>
                <h4 className="mt-5">Miejsce pobrania</h4>
                <p>
                  <span className="fw-bold">Adres: </span>
                  {orderCollection.address}, {orderCollection.postalCode}{' '}
                  {orderCollection.city}
                </p>
                <p>
                  <span className="fw-bold">Numer telefonu: </span>
                  {orderCollection.phoneNumber}
                </p>
              </>
            )}

            {!!orderCollection?.nurseNotes && (
              <>
                <h4 className="mt-5">Notatki osoby pobierającej</h4>
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  {orderCollection.nurseNotes}
                </p>
              </>
            )}

            {order.orderDiscounts.length > 0 && (
              <>
                <h4 className="mt-5">Rabaty</h4>
                <ul>
                  {order.orderDiscounts.map((orderDiscount) => (
                    <li>
                      <span>
                        <strong>{orderDiscount.discount.percent}</strong>
                        <span className="small">%</span>
                      </span>{' '}
                      {getDiscountBadge(orderDiscount.discount.type)} (
                      <span
                        className={`${
                          orderDiscount.discount.deletedAt
                            ? 'text-decoration-line-through'
                            : ''
                        } small`}
                      >
                        {orderDiscount.discount.code}
                      </span>
                      )
                    </li>
                  ))}
                </ul>
              </>
            )}

            {documents.length > 0 && (
              <>
                <h4 className="mt-5">Dokumenty pacjenta</h4>
                <ul className="d-flex list-unstyled">
                  {documents.map((uri) => (
                    <li className="me-2">
                      <a href={uri} target="_blank" rel="noreferrer">
                        <img src={uri} alt="" className="img-thumbnail" />
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
          <div className="flex-grow-1 w-50">
            <h4 className="mt-5">Koszty</h4>
            {!!orderCollection && (
              <>
                <p>
                  <span className="fw-bold">Koszt pobrania: </span>
                  <span>{orderCollection.collectionCost.toFixed(2)} zł</span>
                </p>
                {orderCollection.collectionCostBeforeDiscount && (
                  <p>
                    <span className="fw-bold">
                      Koszt pobrania przed rabatem:{' '}
                    </span>
                    <span>
                      {orderCollection.collectionCostBeforeDiscount.toFixed(2)}{' '}
                      zł
                    </span>
                  </p>
                )}
                <p>
                  <span className="fw-bold">Koszt zestawu: </span>
                  <span>{orderCollection.collectionKitCost.toFixed(2)} zł</span>
                </p>
                <p>
                  <span className="fw-bold">Koszt dostawy: </span>
                  <span>{orderCollection.deliveryCost.toFixed(2)} zł</span>
                </p>
              </>
            )}
            <p>
              <span className="fw-bold">Koszt całkowity: </span>
              <span>{order.totalCost.toFixed(2)} zł</span>
            </p>
            {order.costBeforeDiscount && (
              <p>
                <span className="fw-bold">Koszt przed rabatem: </span>
                <span>{order.costBeforeDiscount.toFixed(2)} zł</span>
              </p>
            )}

            <h4 className="mt-5">Zgody</h4>
            <p>
              <span className="fw-bold">Zgoda 1: </span>
              {order.clientConsent1
                ? `udzielona ${moment(order.firstStateDate).format(
                    'D MMM YYYY HH:mm'
                  )}`
                : 'brak'}
              <p className="small text-muted">
                Wyrażam zgodę na realizację zamówionych przeze mnie badań
                diagnostycznych i/lub pozostałych usług w zakresie opieki
                medycznej, pobranie ode mnie właściwego materiału
                diagnostycznego i wykonanie badań.
              </p>
            </p>
            <p>
              <span className="fw-bold">Zgoda 2 (globalna – Regulamin): </span>
              {order.client.regulationsAcceptedAt
                ? `udzielona ${moment(order.client.regulationsAcceptedAt).format(
                    'D MMM YYYY HH:mm'
                  )}`
                : 'brak'}
              <p className="small text-muted">
                Oświadczam, iż zapoznałam/em się i akceptuję Regulamin
                Aplikacji Home Lab oraz Politykę prywatności Aplikacji Home Lab.
              </p>
            </p>
            <p>
              <span className="fw-bold">Zgoda 3: </span>
              {order.clientConsent3
                ? `udzielona ${moment(order.firstStateDate).format(
                    'D MMM YYYY HH:mm'
                  )}`
                : 'brak'}
              <p className="small text-muted">
                Oświadczam, iż zlecane przeze mnie za pośrednictwem Aplikacji
                Home Lab badania laboratoryjne i/lub pozostałe usługi w zakresie
                opieki medycznej są wykonywane w celu zapewnienia mnie lub
                osobie/małoletniemu pozostającemu pod moją władzą rodzicielską,
                opieką lub kuratelą opieki medycznej w zakresie profilaktyki
                zdrowia, dbania o zdrowie, ratowania zdrowia, przywracaniu i
                poprawie zdrowia.
              </p>
            </p>
            <p>
              <span className="fw-bold">Zgoda 4: </span>
              {order.clientConsent4
                ? `udzielona ${moment(order.firstStateDate).format(
                    'D MMM YYYY HH:mm'
                  )}`
                : 'brak'}
              <p className="small text-muted">
                Oświadczam, iż rozumiem i akceptuję fakt, że żądanie przeze
                mnie pełnego wykonania przez Spółkę opłaconej usługi przed
                upływem terminu do odstąpienia od umowy powoduje utratę przeze
                mnie prawa do odstąpienia od umowy.
              </p>
            </p>
            {shippings.length > 0 && (
              <>
                <p>
                  <span className="fw-bold">Zgoda W1: </span>
                  {order.shippingConsent1
                    ? `udzielona ${moment(order.firstStateDate).format(
                        'D MMM YYYY HH:mm'
                      )}`
                    : 'brak'}
                  <p className="small text-muted">
                    Oświadczam, że zapoznałem się z instrukcją pobrania
                    materiału biologicznego oraz zobowiązuję się do jej
                    przestrzegania.
                  </p>
                </p>
                <p>
                  <span className="fw-bold">Zgoda W2: </span>
                  {order.shippingConsent2
                    ? `udzielona ${moment(order.firstStateDate).format(
                        'D MMM YYYY HH:mm'
                      )}`
                    : 'brak'}
                  <p className="small text-muted">
                    Wyrażam zgodę na samodzielne i dobrowolne pobranie materiału
                    biologicznego.
                  </p>
                </p>
                <p>
                  <span className="fw-bold">Zgoda W3: </span>
                  {order.shippingConsent3
                    ? `udzielona ${moment(order.firstStateDate).format(
                        'D MMM YYYY HH:mm'
                      )}`
                    : 'brak'}
                  <p className="small text-muted">
                    Oświadczam, że jestem świadomy/a skutków nieprawidłowego
                    pobrania materiału biologicznego, w tym możliwości odmowy
                    wykonania badania przez Laboratorium bez prawa do zwrotu
                    ceny.
                  </p>
                </p>
              </>
            )}
          </div>
        </div>

        <h4 className="mt-5">Produkty</h4>
        <ListGroup>
          {orderItems.map((orderItem) => {
            if (isOrderTest(orderItem)) {
              return (
                <ListGroup.Item
                  key={`test-${orderItem.id}`}
                  className={`d-flex justify-content-between align-items-center${orderItem.deletedAt ? ' opacity-50' : ''}`}
                >
                  <Form.Check
                    className="me-1 d-print-none"
                    checked={checkedOrderTestIds.includes(orderItem.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCheckedOrderTestIds([
                          ...checkedOrderTestIds,
                          orderItem.id,
                        ])
                      } else {
                        setCheckedOrderTestIds(
                          checkedOrderTestIds.filter(
                            (orderTestId) => orderTestId !== orderItem.id
                          )
                        )
                      }
                    }}
                  />
                  <div className="ms-2 me-auto w-100">
                    <Row className="mb-3">
                      <Col>
                        <div
                          className={
                            orderItem.test.deletedAt || orderItem.deletedAt
                              ? 'text-decoration-line-through'
                              : ''
                          }
                        >
                          <p className="small m-0">
                            <DropletHalf size={16} className="me-1" />
                            Badanie
                            {orderItem.deletedAt && (
                              <span className="text-danger ms-2 small">
                                (usunięte{' '}
                                {new Date(orderItem.deletedAt).toLocaleString()}
                                )
                              </span>
                            )}
                          </p>
                          <strong>{orderItem.test.name}</strong>
                        </div>
                      </Col>
                      <Col className="d-flex justify-content-end">
                        <Button
                          title="Edytuj"
                          variant="secondary"
                          onClick={() =>
                            setUpdateOrderTestModal({
                              isVisible: true,
                              id: orderItem.id,
                              tubeCode: orderItem.tubeCode,
                              orderId: order?.id,
                              currentOrderState: order?.currentState as Order_State_Enum_Enum,
                              firstName: orderItem.firstName,
                              lastName: orderItem.lastName,
                              identity: orderItem.identity,
                              gender: orderItem.gender,
                            })
                          }
                        >
                          <PencilSquare size={20} />
                        </Button>
                        {orderItem.deletedAt ? (
                          <Button
                            title="Przywróć badanie"
                            variant="outline-success"
                            className="ms-2"
                            onClick={() =>
                              handleRestoreItem('test', orderItem.id)
                            }
                          >
                            <ArrowCounterclockwise size={20} />
                          </Button>
                        ) : (
                          <Button
                            title="Usuń badanie"
                            variant="outline-danger"
                            className="ms-2"
                            onClick={() =>
                              setDeleteItemModal({
                                isVisible: true,
                                itemType: 'test',
                                itemId: orderItem.id,
                                itemName: orderItem.test.name ?? '',
                                itemCost: orderItem.cost ?? 0,
                                itemCostBeforeDiscount:
                                  orderItem.costBeforeDiscount,
                              })
                            }
                          >
                            <Trash size={20} />
                          </Button>
                        )}
                        <Button
                          title="Dodaj wynik"
                          className="ms-2 d-flex align-items-center"
                          onClick={(event) => {
                            event.currentTarget
                              .querySelectorAll('input[type=file]')
                              .forEach((input: any) => input.click())
                          }}
                        >
                          <FileEarmarkPlus size={20} />
                          <input
                            ref={fileInputRef}
                            type="file"
                            className="visually-hidden"
                            multiple
                            accept="application/pdf"
                            onChange={(event) => {
                              if (event.target.files?.length) {
                                event.target.files?.length &&
                                  uploadOrderTestResult(
                                    [orderItem],
                                    event.target.files
                                  )
                              }
                            }}
                          />
                        </Button>
                        {orderItem.retrievedOn && !orderItem.finishedOn && (
                          <Button
                            title="Zakończ badanie"
                            className="ms-2 d-flex align-items-center"
                            variant="success"
                            disabled={sendOrderTestFetching}
                            onClick={async () => {
                              const { error } = await sendOrderTest({
                                id: orderItem.id,
                                now: new Date(),
                              })
                              if (error) {
                                setGeneralError(error.message)
                              } else {
                                refetchGetOrder({ requestPolicy: 'network-only' })
                              }
                            }}
                          >
                            <CheckCircle size={20} />
                          </Button>
                        )}
                      </Col>
                    </Row>

                    <Row className="w-100">
                      <Col xs={{ span: 6 }}>
                        <div className="d-flex align-items-center">
                          <UpcScan size={20} className="me-1" />
                          {orderItem.tubeCode}
                        </div>
                        {orderItem.collectedOn && (
                          <div
                            className="d-flex align-items-center"
                            title="Data pobrania"
                          >
                            <DropletFill size={20} className="me-1" />
                            <span>
                              {new Date(orderItem.collectedOn).toLocaleString()}
                            </span>
                          </div>
                        )}
                        {!!orderItem.cost && (
                          <div className="d-flex align-items-center">
                            <CashCoin size={20} className="me-1" />
                            <span>
                              {orderItem.cost.toFixed(2)} zł
                              {orderItem.costBeforeDiscount && (
                                <>
                                  {' '}
                                  (przed rabatem:{' '}
                                  {orderItem.costBeforeDiscount.toFixed(2)} zł)
                                </>
                              )}
                            </span>
                          </div>
                        )}
                        <div className="d-flex align-items-center">
                          <Person size={20} className="me-1" />
                          <span>
                            {orderItem.firstName} {orderItem.lastName}
                          </span>
                        </div>
                        {orderItem.gender && (
                          <div className="d-flex align-items-center">
                            {orderItem.gender === Gender_Enum.Male && (
                              <>
                                <GenderMale size={20} className="me-1" />
                                <span>Mężczyzna</span>
                              </>
                            )}
                            {orderItem.gender === Gender_Enum.Female && (
                              <>
                                <GenderFemale size={20} className="me-1" />
                                <span>Kobieta</span>
                              </>
                            )}
                          </div>
                        )}
                        <div className="d-flex align-items-center">
                          <PersonVideo3 size={20} className="me-1" />
                          {renderIdentity(orderItem.identity)}
                        </div>
                      </Col>
                      <Col xs={{ span: 6 }}>
                        {orderItem.collectionPoint && (
                          <div className="d-flex align-items-center">
                            <Radioactive size={20} className="me-1" />
                            <span>
                              <a
                                href={`/laboratory/${orderItem.collectionPoint.laboratory.id}`}
                              >
                                {orderItem.collectionPoint.laboratory.shortName}{' '}
                                / {orderItem.collectionPoint.name}
                              </a>
                              , {orderItem.collectionPoint.address},{' '}
                              {orderItem.collectionPoint.city} (
                              {orderItem.collectionPoint.postalCode})
                            </span>
                          </div>
                        )}
                        {orderItem.laboratoryCode && (
                          <div className="d-flex align-items-center">
                            <Tag size={20} className="me-1" />
                            <span>{orderItem.laboratoryCode}</span>
                          </div>
                        )}
                        {orderItem.finishedOn && (
                          <div
                            className="d-flex align-items-center"
                            title="Data wysłania wyniku do klienta"
                          >
                            <CheckCircleFill size={20} className="me-1" />
                            <span>
                              {new Date(orderItem.finishedOn).toLocaleString()}
                            </span>
                          </div>
                        )}
                        {!!orderItem.testResults.length &&
                          orderItem.testResults.map((testResult) => (
                            <div
                              className="d-flex align-items-center"
                              key={testResult.file?.id ?? `${orderItem.id}-${testResult.date}`}
                            >
                              <FileEarmark size={20} className="me-1" />
                              <Button
                                onClick={() => testResult.file && openFile(testResult.file.id)}
                                className="p-0"
                                variant="link"
                                disabled={!testResult.file}
                              >
                                {testResult.originalFilename}
                              </Button>
                              {testResult.file && (
                              <Button
                                variant="link"
                                className="p-0 d-print-none"
                                onClick={async () => {
                                  if (
                                    window.confirm(
                                      'Czy napewno chcesz usunąć wynik badania?'
                                    )
                                  ) {
                                    const { error } =
                                      await deleteOrderTestResult({
                                        fileId: testResult.file!.id,
                                        orderTestId: orderItem.id,
                                      })
                                    if (error) {
                                      setGeneralError(error.message)
                                      return
                                    }

                                    refetchGetOrder({
                                      requestPolicy: 'network-only',
                                    })
                                  }
                                }}
                              >
                                <X size={22} color="#f56565" />
                              </Button>
                              )}
                            </div>
                          ))}
                      </Col>
                    </Row>
                  </div>
                </ListGroup.Item>
              )
            } else if (isOrderConsultation(orderItem)) {
              return (
                <ListGroup.Item
                  key={`consultation-${orderItem.id}`}
                  className={orderItem.deletedAt ? 'opacity-50' : ''}
                >
                  <Row className="ms-3 mb-3">
                    <Col>
                      <div
                        className={
                          orderItem.consultation.deletedAt || orderItem.deletedAt
                            ? 'text-decoration-line-through'
                            : ''
                        }
                      >
                        <p className="small m-0">
                          <Headset size={16} className="me-1" />
                          Konsultacja
                          {orderItem.deletedAt && (
                            <span className="text-danger ms-2 small">
                              (usunięte{' '}
                              {new Date(orderItem.deletedAt).toLocaleString()})
                            </span>
                          )}
                        </p>
                        <strong>{orderItem.consultation.name}</strong>
                      </div>
                    </Col>
                    <Col className="d-flex justify-content-end gap-2">
                      {!orderItem.finishedOn &&
                        !['Unpaid', 'Cancelled'].includes(
                          order?.currentState || ''
                        ) && (
                          <>
                            <Button
                              variant="success"
                              className="d-flex align-items-center"
                              onClick={async () => {
                                if (
                                  window.confirm(
                                    'Czy napewno chcesz zakończyć konsultację?'
                                  )
                                ) {
                                  const { error } =
                                    await updateOrderConsultationFinishedOn({
                                      id: orderItem.id,
                                    })
                                  if (error) {
                                    setGeneralError(error.message)
                                    return
                                  }

                                  refetchGetOrder({
                                    requestPolicy: 'network-only',
                                  })
                                }
                              }}
                            >
                              <CheckCircleFill size={20} className="me-1" />
                              Zakończ konsultację
                            </Button>
                          </>
                        )}
                      {orderItem.deletedAt ? (
                        <Button
                          title="Przywróć konsultację"
                          variant="outline-success"
                          onClick={() =>
                            handleRestoreItem('consultation', orderItem.id)
                          }
                        >
                          Przywróć
                        </Button>
                      ) : (
                        <Button
                          title="Usuń konsultację"
                          variant="outline-danger"
                          onClick={() =>
                            setDeleteItemModal({
                              isVisible: true,
                              itemType: 'consultation',
                              itemId: orderItem.id,
                              itemName: orderItem.consultation.name ?? '',
                              itemCost: orderItem.cost ?? 0,
                              itemCostBeforeDiscount: null,
                            })
                          }
                        >
                          <Trash size={20} />
                        </Button>
                      )}
                    </Col>
                  </Row>

                  <Row className="ms-3 w-100">
                    <Col xs={{ span: 6 }}>
                      {orderItem.cost && (
                        <div className="d-flex align-items-center">
                          <CashCoin size={20} className="me-1" />
                          <span>{orderItem.cost.toFixed(2)} zł</span>
                        </div>
                      )}
                      <div className="d-flex align-items-center">
                        <Person size={20} className="me-1" />
                        <span>
                          {orderItem.firstName} {orderItem.lastName}
                        </span>
                      </div>
                      {orderItem.gender && (
                        <div className="d-flex align-items-center">
                          {orderItem.gender === Gender_Enum.Male && (
                            <>
                              <GenderMale size={20} className="me-1" />
                              <span>Mężczyzna</span>
                            </>
                          )}
                          {orderItem.gender === Gender_Enum.Female && (
                            <>
                              <GenderFemale size={20} className="me-1" />
                              <span>Kobieta</span>
                            </>
                          )}
                        </div>
                      )}
                      <div className="d-flex align-items-center">
                        <PersonVideo3 size={20} className="me-1" />
                        {renderIdentity(orderItem.identity)}
                      </div>
                    </Col>
                    <Col xs={{ span: 6 }}>
                      {orderItem.preferredDate && (
                        <div
                          className="d-flex align-items-center"
                          title="Wybrana data konsultacji"
                        >
                          <CalendarEvent size={20} className="me-1" />
                          <span>
                            {new Date(orderItem.preferredDate).toLocaleString()}
                          </span>
                        </div>
                      )}
                      <div
                        className="d-flex align-items-center"
                        title="Nr telefonu"
                      >
                        <Telephone size={20} className="me-1" />
                        <span>{orderItem.phoneNumber}</span>
                      </div>
                      {!!orderItem.finishedOn && (
                        <div
                          className="d-flex align-items-center"
                          title="Data zakończenia konsultacji"
                        >
                          <CheckCircleFill size={20} className="me-1" />
                          <span>
                            {new Date(orderItem.finishedOn).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </Col>
                  </Row>
                </ListGroup.Item>
              )
            } else if (isOrderTraining(orderItem)) {
              return (
                <ListGroup.Item
                  key={`training-${orderItem.id}`}
                  className={orderItem.deletedAt ? 'opacity-50' : ''}
                >
                  <Row className="ms-3 mb-3">
                    <Col>
                      <div
                        className={
                          orderItem.training.deletedAt || orderItem.deletedAt
                            ? 'text-decoration-line-through'
                            : ''
                        }
                      >
                        <p className="small m-0">
                          <CameraVideo size={16} className="me-1" />
                          Szkolenie
                          {orderItem.deletedAt && (
                            <span className="text-danger ms-2 small">
                              (usunięte{' '}
                              {new Date(orderItem.deletedAt).toLocaleString()})
                            </span>
                          )}
                        </p>

                        <strong>{orderItem.training.name}</strong>
                      </div>
                    </Col>
                    <Col className="d-flex justify-content-end">
                      {orderItem.deletedAt ? (
                        <Button
                          title="Przywróć szkolenie"
                          variant="outline-success"
                          onClick={() =>
                            handleRestoreItem('training', orderItem.id)
                          }
                        >
                          Przywróć
                        </Button>
                      ) : (
                        <Button
                          title="Usuń szkolenie"
                          variant="outline-danger"
                          onClick={() =>
                            setDeleteItemModal({
                              isVisible: true,
                              itemType: 'training',
                              itemId: orderItem.id,
                              itemName: orderItem.training.name ?? '',
                              itemCost: orderItem.cost ?? 0,
                              itemCostBeforeDiscount: null,
                            })
                          }
                        >
                          <Trash size={20} />
                        </Button>
                      )}
                    </Col>
                  </Row>
                  <Row className="ms-3 w-100">
                    <Col xs={{ span: 6 }}>
                      {orderItem.cost && (
                        <div className="d-flex align-items-center">
                          <CashCoin size={20} className="me-1" />
                          <span>{orderItem.cost.toFixed(2)} zł</span>
                        </div>
                      )}
                    </Col>
                    <Col xs={{ span: 6 }}></Col>
                  </Row>
                </ListGroup.Item>
              )
            } else {
              return <span>unknown item</span>
            }
          })}
        </ListGroup>
        {checkedOrderTestIds.length > 0 && (
          <Button
            title="Prześlij wyniki dla wybranych badań"
            className="mt-2 d-flex align-items-center d-print-none"
            onClick={(event) => {
              event.currentTarget
                .querySelectorAll('input[type=file]')
                .forEach((input: any) => input.click())
            }}
          >
            <Upload size={20} className="me-2" />
            Prześlij wyniki dla wybranych badań
            <input
              ref={fileInputRef}
              type="file"
              className="visually-hidden"
              multiple
              accept="application/pdf"
              onChange={async (event) => {
                if (event.target.files?.length && data.order_by_pk) {
                  await uploadOrderTestResult(
                    data.order_by_pk.orderTests.filter((orderTest) =>
                      checkedOrderTestIds.includes(orderTest.id)
                    ),
                    event.target.files
                  )

                  setCheckedOrderTestIds([])
                }
              }}
            />
          </Button>
        )}

        <h4 className="mt-5">Przesyłki</h4>
        <ListGroup>
          {shippings.map((s) => {
            return (
              <ListGroup.Item key={`shipping-${s.id}`}>
                <div className="p-2">
                  <Col>
                    <Row>
                      <div className="d-flex flex-row justify-content-between">
                        <strong className="m-0 d-flex align-items-center gap-2">
                          <Box size={20} className="me-1" />
                          Wysyłka do{' '}
                          {s.direction === 'to_lab'
                            ? 'laboratorium'
                            : 'klienta'}
                        </strong>
                        <Button
                          onClick={() =>
                            getShipmentLabel(s.id, s.trackingNumber)
                          }
                        >
                          Pobierz etykietę
                        </Button>
                      </div>
                    </Row>
                    <Row>
                      <p className="m-0 d-flex align-items-center gap-2">
                        <CashCoin size={20} className="me-1" />
                        <span>
                          {s.cost.toFixed(2)} zł
                          {s.costBeforeDiscount && (
                            <>
                              {' '}
                              (przed rabatem: {s.costBeforeDiscount.toFixed(
                                2
                              )}{' '}
                              zł)
                            </>
                          )}
                        </span>
                      </p>
                      <p className="m-0 d-flex align-items-center gap-2">
                        <Envelope size={20} className="me-1" />
                        <span>{s.email}</span>
                      </p>
                      <p className="m-0 d-flex align-items-center gap-2">
                        <Phone size={20} className="me-1" />
                        <span>{s.phone}</span>
                      </p>{' '}
                      <p className="m-0 d-flex align-items-center gap-2">
                        <Archive size={20} className="me-1" />
                        <span>
                          <strong>{s.pointName}</strong>
                        </span>
                      </p>
                      <p className="m-0 d-flex align-items-center gap-2">
                        <Upc size={20} className="me-1" />
                        <span>
                          <strong>{s.trackingNumber}</strong>
                        </span>
                      </p>
                      <InpostStatusInfo
                        status={getInpostStatusByStatusName(s.status)}
                      />
                    </Row>
                  </Col>
                </div>
              </ListGroup.Item>
            )
          })}
        </ListGroup>

        <h4 className="mt-5">Historia statusów</h4>
        <ListGroup className="w-50">
          {order.orderStates
            .filter(
              (orderState, currentIndex, array) =>
                array[currentIndex].state !==
                  Order_State_Enum_Enum.OnTheWayToClient ||
                currentIndex === 0 ||
                array[currentIndex - 1].state !== array[currentIndex].state
            )
            .map((orderState) => (
              <ListGroup.Item className="" key={orderState.id}>
                <div className="ms-2 me-auto d-flex gap-2">
                  <div className="d-flex align-items-center">
                    <CalendarDate size={16} className="me-1" />
                    <span className="text-muted small">
                      {new Date(orderState.date).toLocaleString()}
                    </span>
                  </div>
                  <Badge bg={OrderState2BgVariant[orderState.state]}>
                    {OrderState2Label[orderState.state]}
                  </Badge>
                </div>
                <div className="ms-2">
                  {orderState.state ===
                    Order_State_Enum_Enum.DifferentDateProposed && (
                    <span>
                      Zaproponowana data pobrania:{' '}
                      <strong>
                        {moment(orderState.extra.proposedDate).format(
                          'yyyy-MM-DD HH:mm'
                        )}
                      </strong>
                    </span>
                  )}
                </div>
              </ListGroup.Item>
            ))}
        </ListGroup>
      </>

      <TestResultConfirmationModal
        ref={testResultConfirmationModalRef}
        onFinished={() => {
          refetchGetOrder({ requestPolicy: 'network-only' })
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        }}
      />

      <OrderNurseModal
        ref={orderNurseModalRef}
        onChange={() => refetchGetOrder({ requestPolicy: 'network-only' })}
      />
      <OrderTestModal
        data={updateOrderTestModal}
        onClose={() => setUpdateOrderTestModal({ isVisible: false })}
      />
      <ChangeOrderStatusModal
        ref={changeOrderStatusModalRef}
        onChange={() => refetchGetOrder({ requestPolicy: 'network-only' })}
      />
      <DeleteOrderItemModal
        show={deleteItemModal.isVisible}
        itemName={deleteItemModal.itemName}
        itemCost={deleteItemModal.itemCost}
        onConfirm={handleDeleteItem}
        onHide={() =>
          setDeleteItemModal((prev) => ({ ...prev, isVisible: false }))
        }
        isLoading={deleteItemLoading}
      />
    </div>
  )
}

export default Order

type InpostStatusInfoProps = { status?: InpostStatus }

const InpostStatusInfo = ({ status }: InpostStatusInfoProps) => {
  return (
    <div className="d-flex flex-column mt-2 ">
      <span>
        <strong>Status z Inpost</strong>
      </span>
      {status ? (
        <>
          <span>{status.title}</span>
          <span>{status.description}</span>
        </>
      ) : (
        <span>Brak informacji o statusie przesyłki</span>
      )}
    </div>
  )
}
